/**
 * TeamLeader Workspace — Student Profile Page
 * Dedicated tabbed student profile page (Overview, Courses, Tracks, Exams, Certificates, Messages, CRM).
 * Every tab loads independently.
 */
import { createFileRoute, useNavigate, useParams, Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useStudentProfile } from "@/hooks/use-tl-students";
import { useTlStudentExamProfile } from "@/hooks/use-tl-exams";
import { fetchStudentActivities } from "@/lib/api";
import { StudentActivityLog } from "@/components/student-activity-log";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StudentAssignmentsPanel } from "@/components/assignments/student-assignments-panel";
import { StudentAttemptTimeline } from "@/components/team-leader/exams/StudentAttemptTimeline";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  ArrowLeft,
  GraduationCap,
  BookOpen,
  Award,
  MessageSquare,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Lock,
  PlayCircle,
  Layers,
  CircleDashed,
  ClipboardList,
  ChevronRight,
  Users,
  NotebookPen,
  Target,
  BellRing,
  Phone,
  ShieldAlert,
  TrendingDown,
} from "lucide-react";
import type {
  TlCertificate,
  TlStudentCourseProgress,
  TlStudentModuleRef,
} from "@/lib/api/tl-exams";

/**
 * The five panels of this page. `?tab=` is validated against this list so an unknown value lands on
 * Overview rather than rendering nothing.
 */
const STUDENT_PROFILE_TABS = ["overview", "courses", "exams", "assignments", "crm"] as const;
type StudentProfileTab = (typeof STUDENT_PROFILE_TABS)[number];

export const Route = createFileRoute("/_authenticated/team-leader/students_/$studentId")({
  /**
   * The active tab lives in the URL so this page can be linked to at a specific panel — the exam
   * workspace's Student Sheet points at `?tab=exams`, and so does the compatibility redirect from
   * the retired `/team-leader/exams/student/$studentId`. Until now the tab was `useState`, so both
   * of those would have landed on Overview and silently lost the exam context.
   *
   * Existing links pass no `tab` and behave exactly as before.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: StudentProfileTab } => ({
    tab:
      typeof search.tab === "string" &&
      (STUDENT_PROFILE_TABS as readonly string[]).includes(search.tab)
        ? (search.tab as StudentProfileTab)
        : undefined,
  }),
  component: StudentProfilePage,
});

// ── Small presentation helpers ────────────────────────────────────────────────

/** "3 days ago", or "—" when the timestamp is absent. Never invents a date. */
function ago(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : formatDistanceToNow(d, { addSuffix: true });
}

/** Seconds → "4h 12m" / "12m". Used for course content length. */
function formatDuration(totalSeconds: number): string {
  if (!totalSeconds) return "—";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.round((totalSeconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Presence, mirroring the roster's three states so a student reads the same on both pages:
 * Online (seen inside the server's 5-minute window), Offline (seen before it), NeverActive (no
 * LastSeenAt at all — the account exists but has never been signed into).
 */
const PRESENCE = {
  Online: {
    label: "Online",
    dot: "bg-success",
    text: "text-success",
    hint: "Seen in the last 5 minutes",
  },
  Offline: {
    label: "Offline",
    dot: "bg-muted-foreground/50",
    text: "text-muted-foreground",
    hint: "Has signed in before, but not in the last 5 minutes",
  },
  NeverActive: {
    label: "Never signed in",
    dot: "border border-warning bg-transparent",
    text: "text-warning",
    hint: "This account has never been signed into",
  },
} as const;

/** Unknown/absent `activity` values fall back to Offline rather than inventing a presence. */
function presenceOf(activity: string | null | undefined) {
  return PRESENCE[
    (activity as keyof typeof PRESENCE) in PRESENCE
      ? (activity as keyof typeof PRESENCE)
      : "Offline"
  ];
}

/**
 * One CRM contact-log entry, as `fetchStudentActivities` normalises it — snake_case, because that
 * mapper converts the API's camelCase payload into the shape the older activity-log UI reads.
 * The API helper itself returns `any[]`, so the shape is restated here rather than inferred.
 */
type ContactLogEntry = {
  id: string;
  action: string;
  outcome: string;
  notes: string | null;
  created_at: string;
  actor?: { id: string | null; full_name: string | null; role: string | null };
};

// Contact-log vocabulary, mirroring `StudentActivityLog`'s own maps so the summary in the
// mentoring column and the full log in the CRM tab name the same entry the same way.
const CONTACT_LABELS: Record<string, string> = {
  called: "Called",
  messaged: "Sent message",
  met: "Met",
  dashboard_reminder: "Asked to open dashboard",
  other: "Other",
};

const CONTACT_OUTCOMES: Record<string, string> = {
  reached: "Reached",
  no_answer: "No answer",
  follow_up: "Follow-up needed",
  none: "",
};

const CONTACT_ICON: Record<string, typeof Phone> = {
  called: Phone,
  messaged: MessageSquare,
  met: Users,
  dashboard_reminder: BellRing,
  other: FileText,
};

/** Whole days between `iso` and now, or null when there is no usable timestamp. */
function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

/**
 * One labelled module slot on a course card (Last opened / Last completed / Next up).
 * Renders the empty text rather than an em dash when there is no module, so "never started"
 * and "finished everything" read differently.
 */
function ModuleSlot({
  icon,
  label,
  module,
  empty,
  timestamp,
}: {
  icon: ReactNode;
  label: string;
  module?: TlStudentModuleRef | null;
  empty: string;
  timestamp?: "lastAccessedAt" | "completedAt";
}) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        {icon} {label}
      </p>
      {module ? (
        <>
          <p className="text-sm font-medium truncate" title={module.title}>
            {module.title}
          </p>
          <p className="text-xs text-muted-foreground">
            {timestamp ? ago(module[timestamp]) : `${module.progressPercentage}% watched`}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

/** Status pill for a course: locked and completed both outrank the module counts. */
function CourseStatusBadge({ course }: { course: TlStudentCourseProgress }) {
  if (course.isLocked) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] border-amber-500/40 text-amber-600 gap-1"
        title={
          course.blockingCourseName
            ? `Locked until "${course.blockingCourseName}" is complete`
            : (course.lockReason ?? undefined)
        }
      >
        <Lock className="h-3 w-3" /> Locked
      </Badge>
    );
  }
  if (course.isCompleted) {
    return (
      <Badge className="text-[10px] gap-1">
        <CheckCircle2 className="h-3 w-3" /> Completed
      </Badge>
    );
  }
  if (course.completedModules > 0) {
    return (
      <Badge variant="secondary" className="text-[10px]">
        In progress
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      Not started
    </Badge>
  );
}

/** Full detail card for one course the student can reach. */
function CourseProgressCard({
  course,
  studentId,
}: {
  course: TlStudentCourseProgress;
  studentId: string;
}) {
  // A course can sit at 100 % of its modules and still not be "complete" — the exam gates
  // (checkpoints + final) are separate. Call that out rather than letting the full bar imply done.
  const modulesDoneButGated =
    !course.isCompleted &&
    course.totalModules > 0 &&
    course.completedModules === course.totalModules;

  return (
    <Card className={course.isLocked ? "opacity-75" : undefined}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base truncate" title={course.courseTitle}>
              {/* The course name is the way into this student's module list for this course —
                  select, then complete or send back to restudy. It replaced a collapsible panel
                  that lived in this card and could only complete. */}
              <Link
                to="/team-leader/students/$studentId/courses/$courseId"
                params={{ studentId, courseId: course.courseId }}
                className="hover:underline underline-offset-4"
              >
                {course.courseTitle}
              </Link>
            </CardTitle>
            <CardDescription className="text-xs flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
              {course.trackName && (
                <span className="inline-flex items-center gap-1">
                  <Layers className="h-3 w-3" />
                  {course.trackName}
                  {course.trackSequence != null && ` · #${course.trackSequence}`}
                </span>
              )}
              <span>{formatDuration(course.totalDurationSeconds)} of content</span>
              <span>Last activity {ago(course.lastActivityAt)}</span>
            </CardDescription>
          </div>
          <CourseStatusBadge course={course} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold">{course.completionPercent}%</span>
            <span className="text-xs text-muted-foreground">
              {course.completedModules}/{course.totalModules} modules
              {course.inProgressModules > 0 && ` · ${course.inProgressModules} in progress`}
              {course.watchedHours > 0 && ` · ${course.watchedHours} hrs watched`}
            </span>
          </div>
          <Progress value={course.completionPercent} className="h-2" />
        </div>

        {modulesDoneButGated && (
          <p className="text-xs text-amber-600 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            All modules watched — course still open until the exam gates are passed.
          </p>
        )}

        {course.isLocked && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {course.blockingCourseName
              ? `Locked until "${course.blockingCourseName}" is complete.`
              : "Locked by track sequencing."}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1 border-t">
          <div className="pt-3">
            <ModuleSlot
              icon={<PlayCircle className="h-3 w-3" />}
              label="Last opened"
              module={course.lastAccessedModule}
              empty="Never opened"
              timestamp="lastAccessedAt"
            />
          </div>
          <div className="pt-3">
            <ModuleSlot
              icon={<CheckCircle2 className="h-3 w-3" />}
              label="Last completed"
              module={course.lastCompletedModule}
              empty="Nothing completed yet"
              timestamp="completedAt"
            />
          </div>
          <div className="pt-3">
            <ModuleSlot
              icon={<CircleDashed className="h-3 w-3" />}
              label="Next up"
              module={course.nextModule}
              empty={course.totalModules === 0 ? "No published modules" : "All modules done"}
            />
          </div>
        </div>

        {course.currentCheckpointName && (
          <div className="flex items-center justify-between gap-3 rounded-lg border p-2.5">
            <p className="text-xs text-muted-foreground truncate">
              Checkpoint:{" "}
              <span className="font-medium text-foreground">{course.currentCheckpointName}</span>
            </p>
            {course.checkpointStatus && (
              <Badge variant="outline" className="text-[10px] shrink-0">
                {course.checkpointStatus}
              </Badge>
            )}
          </div>
        )}

        <div className="border-t pt-3">
          <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs">
            <Link
              to="/team-leader/students/$studentId/courses/$courseId"
              params={{ studentId, courseId: course.courseId }}
            >
              <ChevronRight className="h-3.5 w-3.5" />
              Manage modules
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One tab in the profile's underline tab bar.
 *
 * The pill TabsList this replaced wrapped to two rows at laptop widths with the counts inside the
 * labels; an underline bar holds five tabs on one line and lets the count sit in its own chip.
 */
function ProfileTab({
  value,
  icon: Icon,
  label,
  count,
}: {
  value: StudentProfileTab;
  icon: typeof User;
  label: string;
  count?: number;
}) {
  return (
    <TabsTrigger
      value={value}
      className="gap-2 rounded-none border-b-2 border-transparent bg-transparent px-0.5 pb-2.5 pt-0 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-primary data-[state=active]:shadow-none"
    >
      <Icon className="h-4 w-4" />
      {label}
      {count != null && (
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-bold leading-4 text-muted-foreground">
          {count}
        </span>
      )}
    </TabsTrigger>
  );
}

/**
 * One figure in the signal strip.
 *
 * Every metric states what it was measured against (`sub`) — "54%" alone is a number, "54%, 13 of
 * 24 modules" is a fact a mentor can act on. `percent` is optional: learning hours has no
 * denominator to draw a bar against, and inventing one would misrepresent it.
 */
function SignalMetric({
  label,
  value,
  sub,
  percent,
  isLoading,
}: {
  label: string;
  value: string;
  sub: string;
  percent?: number;
  isLoading?: boolean;
}) {
  return (
    <div className="min-w-0 flex-1 space-y-2 px-4 py-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
        {label}
      </p>
      {isLoading ? (
        <Skeleton className="h-7 w-20" />
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-extrabold tabular-nums tracking-tight">{value}</span>
          <span className="truncate text-xs text-muted-foreground">{sub}</span>
        </div>
      )}
      {percent != null && <Progress value={percent} className="h-1.5 bg-muted" />}
    </div>
  );
}

function StudentProfilePage() {
  const { studentId } = useParams({ from: "/_authenticated/team-leader/students_/$studentId" });
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const activeTab: StudentProfileTab = tab ?? "overview";
  const setActiveTab = (next: string) =>
    navigate({
      to: "/team-leader/students/$studentId",
      params: { studentId },
      search: { tab: next === "overview" ? undefined : (next as StudentProfileTab) },
      replace: true,
    });

  // Queries
  const { data: student, isLoading: profileLoading } = useStudentProfile(studentId);

  // Courses/tracks/exams/checkpoints/certificates all come from the one TeamLeader-scoped
  // endpoint that actually exists: GET /teamleader/exams/students/{id}/profile.
  //
  // The tabs used to call /Enrollments/student/{id} and /Exams/submissions/student/{id} — neither
  // route exists on the API. Both client helpers answer `return []` on a non-OK response, so the
  // 404s were swallowed and the Courses and Exams tabs rendered a permanent "nothing here".
  const {
    data: examProfile,
    isLoading: examProfileLoading,
    isError: examProfileError,
    refetch: refetchExamProfile,
  } = useTlStudentExamProfile(studentId);

  const currentCourses = examProfile?.currentCourses ?? [];
  const currentTracks = examProfile?.currentTracks ?? [];
  // Memoized so the `?? []` fallback doesn't hand a fresh array to courseGroups' useMemo on
  // every render, which would defeat it entirely.
  const courseProgress = useMemo(
    () => examProfile?.courseProgress ?? [],
    [examProfile?.courseProgress],
  );
  // Memoized for the same reason as `courseProgress` above: the `?? []` fallback would otherwise
  // hand `scoreTrend`'s useMemo a fresh array on every render and defeat it entirely.
  const examHistory = useMemo(() => examProfile?.examHistory ?? [], [examProfile?.examHistory]);
  const assignedExams = examProfile?.assignedExams ?? [];
  const checkpointProgress = examProfile?.checkpointProgress ?? [];
  const certificates = examProfile?.certificates ?? [];

  // Same queryKey/queryFn as StudentActivityLog, so React Query serves both from one cache entry
  // and the tab badge always matches what the log renders.
  const { data: activityEntries = [] } = useQuery({
    queryKey: ["student-activities", studentId],
    queryFn: () => fetchStudentActivities(studentId),
    enabled: !!studentId,
  });

  // Newest first, by the timestamp rather than by the order the endpoint happened to return —
  // the mentoring column calls its top entry "the last contact", which is only true if it is
  // actually sorted. Entries with an unparsable date sink to the bottom instead of jumping to it.
  const contactLog = useMemo(() => {
    const at = (e: ContactLogEntry) => {
      const t = new Date(e.created_at).getTime();
      return Number.isNaN(t) ? -Infinity : t;
    };
    return [...(activityEntries as ContactLogEntry[])].sort((a, b) => at(b) - at(a));
  }, [activityEntries]);

  // Standalone courses first, then one section per track. The payload already arrives sorted by
  // (track, sequence, title), so grouping preserves the admin's configured roadmap order.
  const courseGroups = useMemo(() => {
    const standalone: TlStudentCourseProgress[] = [];
    const byTrack = new Map<string, TlStudentCourseProgress[]>();
    for (const c of courseProgress) {
      if (c.trackName) {
        const bucket = byTrack.get(c.trackName) ?? [];
        bucket.push(c);
        byTrack.set(c.trackName, bucket);
      } else {
        standalone.push(c);
      }
    }
    return { standalone, tracks: [...byTrack.entries()] };
  }, [courseProgress]);

  /**
   * The last six scored attempts, oldest first — a trajectory rather than a list of numbers.
   * Unscored sittings are dropped rather than plotted as zero, which would read as a total
   * failure instead of "no result yet", and the payload's own order is not trusted: attempts are
   * re-sorted by when they actually happened.
   */
  const scoreTrend = useMemo(
    () =>
      examHistory
        .filter((e) => e.score != null)
        .map((e) => {
          const when = new Date(e.completedAt ?? e.startedAt);
          return {
            key: e.sessionId,
            title: e.examTitle,
            score: Math.min(100, Math.max(0, Number(e.score))),
            passed: e.passed,
            at: when.getTime(),
            label: Number.isNaN(when.getTime())
              ? "—"
              : when.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
          };
        })
        .filter((p) => Number.isFinite(p.at) && Number.isFinite(p.score))
        .sort((a, b) => a.at - b.at)
        .slice(-6),
    [examHistory],
  );

  if (profileLoading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Risk is NOT a training-lifecycle value. TrainingStatus is a mutually-exclusive lifecycle
  // (InTraining/Passed/Failed/Withdrawn*/NotRecommended) and never carries "at_risk" — the old
  // comparisons against "at_risk"/"AtRisk" could not match anything, so that badge was dead code.
  // The real signals are the two orthogonal admin flags, rendered as their own badges below.

  // "InTraining" → "In Training"
  const trainingStatusLabel = student?.status
    ? student.status.replace(/([a-z])([A-Z])/g, "$1 $2")
    : null;

  const presence = presenceOf(student?.activity);
  const lastSeenLabel = student?.lastSeenAt ? ago(student.lastSeenAt) : null;

  // Exams the student has spent every attempt on without passing. `attemptsRemaining` and
  // `lastPassed` both come off the assigned-exam payload, so this is a read of what the API
  // already says rather than a rule invented here.
  const blockedExams = assignedExams.filter(
    (e) => e.attemptsRemaining === 0 && e.lastPassed !== true,
  );
  const inactiveDays = daysSince(examProfile?.lastActivityAt);
  const lastContact: ContactLogEntry | undefined = contactLog[0];

  /**
   * "Your next move" — the single most pressing thing about this student, stated from data the
   * profile already holds. Every branch names a fact and offers the action that follows from it;
   * nothing here infers a reason the payload cannot support, and when none of the conditions hold
   * the card says so rather than manufacturing urgency.
   */
  const nextMove: {
    tone: "danger" | "warn" | "calm";
    icon: typeof BellRing;
    title: string;
    body: string;
    actions: { label: string; icon: typeof Phone; onClick: () => void; primary?: boolean }[];
  } = (() => {
    const message = {
      label: "Send message",
      icon: MessageSquare,
      onClick: () => navigate({ to: "/messages", search: { dmUserId: studentId } }),
    };
    const logContact = { label: "Log contact", icon: Phone, onClick: () => setActiveTab("crm") };

    if (student?.isBlacklisted) {
      return {
        tone: "danger" as const,
        icon: ShieldAlert,
        title: "Blacklisted",
        body: "This account is blocked from the platform. Only an admin can lift the flag — raise it with them if the block no longer applies.",
        actions: [{ ...logContact, primary: true }, message],
      };
    }
    if (student?.isNotResponding) {
      return {
        tone: "warn" as const,
        icon: BellRing,
        title: "Flagged as not responding",
        body: lastContact?.created_at
          ? `Last logged contact was ${ago(lastContact.created_at)}. A call is the usual next escalation.`
          : "Nothing has been logged against this student yet. Try a call, and record the outcome so the next mentor can see it.",
        actions: [{ ...logContact, primary: true }, message],
      };
    }
    if (blockedExams.length > 0) {
      return {
        tone: "warn" as const,
        icon: XCircle,
        title: `Out of attempts on ${blockedExams.length} exam${blockedExams.length === 1 ? "" : "s"}`,
        body: `${blockedExams.map((e) => e.title).join(", ")} — nothing moves until you grant a reattempt or send the course back for restudy.`,
        actions: [
          {
            label: "Open exams",
            icon: BookOpen,
            onClick: () => setActiveTab("exams"),
            primary: true,
          },
          message,
        ],
      };
    }
    if (inactiveDays != null && inactiveDays >= 7) {
      return {
        tone: "warn" as const,
        icon: Clock,
        title: `No module activity in ${inactiveDays} days`,
        body: "Progress has stalled. A check-in now is cheaper than a withdrawal later.",
        actions: [{ ...message, primary: true }, logContact],
      };
    }
    return {
      tone: "calm" as const,
      icon: CheckCircle2,
      title: "Nothing needs chasing",
      body:
        inactiveDays != null
          ? `No flags, attempts in hand, and module activity ${inactiveDays === 0 ? "today" : `${inactiveDays} day${inactiveDays === 1 ? "" : "s"} ago`}.`
          : "No flags and no blocked exams.",
      actions: [message, logContact],
    };
  })();

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
      {/* Identity header.
          Group, track, presence and joined date sit inline here as facts rather than as cards of
          their own further down — the Overview tab used to spend two of its four hero cards
          restating what is really just the top of the page. */}
      <div className="flex flex-col gap-4 rounded-xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 border"
          onClick={() => navigate({ to: "/team-leader/students" })}
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Back to the roster</span>
        </Button>

        <div className="relative shrink-0">
          <Avatar className="h-14 w-14">
            {/* `avatarUrl` is on the profile DTO and was previously ignored, so every student
                rendered as an initial even when they had uploaded a picture. */}
            {student?.avatarUrl && <AvatarImage src={student.avatarUrl} alt="" />}
            <AvatarFallback
              className="text-lg font-bold text-white"
              style={{ background: "var(--gradient-brand)" }}
            >
              {(student?.fullName || student?.email || "?").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ring-2 ring-card ${presence.dot}`}
            title={`${presence.label} — ${presence.hint}`}
          />
          <span className="sr-only">{presence.label}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {student?.fullName || student?.email}
            </h1>
            {trainingStatusLabel && <Badge variant="secondary">{trainingStatusLabel}</Badge>}
            {student?.isBlacklisted && <Badge variant="destructive">Blacklisted</Badge>}
            {student?.isNotResponding && (
              <Badge className="border-transparent bg-warning-muted text-warning hover:bg-warning-muted">
                Not Responding
              </Badge>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="truncate">{student?.email}</span>
            {student?.studentCode && (
              <span className="font-mono text-[11px] text-muted-foreground/70">
                {student.studentCode}
              </span>
            )}
            <span className="hidden h-3 w-px bg-border sm:block" />
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
              {student?.groupNames || "No group"}
            </span>
            <span className="flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
              {currentTracks.length > 0 ? currentTracks.join(", ") : "No track"}
            </span>
            <span className="hidden h-3 w-px bg-border sm:block" />
            <span className="flex items-center gap-1.5">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${presence.dot}`}
                aria-hidden="true"
              />
              <span className={presence.text}>{presence.label}</span>
              {lastSeenLabel && <span>· {lastSeenLabel}</span>}
            </span>
            {student?.createdAt && (
              <span className="text-muted-foreground/70">
                · Joined {new Date(student.createdAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setActiveTab("crm")}>
            <NotebookPen className="h-4 w-4" /> Log contact
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => navigate({ to: "/messages", search: { dmUserId: studentId } })}
          >
            <MessageSquare className="h-4 w-4" /> Send message
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0 flex-wrap">
          <ProfileTab value="overview" icon={User} label="Overview" />
          <ProfileTab
            value="courses"
            icon={GraduationCap}
            label="Courses & Tracks"
            count={currentCourses.length + currentTracks.length}
          />
          <ProfileTab
            value="exams"
            icon={BookOpen}
            label="Exams"
            count={assignedExams.length || examHistory.length}
          />
          <ProfileTab value="assignments" icon={ClipboardList} label="Assignments" />
          <ProfileTab value="crm" icon={FileText} label="CRM" count={activityEntries.length} />
        </TabsList>

        {/* The signal strip sits under the tab bar rather than inside Overview: these five figures
            are the frame for every panel, and the eight hero cards they replace cost a full screen
            of height before any of them stated what its number was measured against. */}
        <div className="flex flex-col divide-y overflow-hidden rounded-xl border bg-card shadow-sm sm:flex-row sm:divide-x sm:divide-y-0">
          <SignalMetric
            label="Module progress"
            value={`${Math.round(examProfile?.overallCompletionPercent ?? student?.progressPercent ?? 0)}%`}
            sub={`${examProfile?.totalModulesCompleted ?? 0} of ${examProfile?.totalModulesAssigned ?? 0} modules`}
            percent={examProfile?.overallCompletionPercent ?? student?.progressPercent ?? 0}
            isLoading={examProfileLoading}
          />
          {/* Distinct from module progress: a course only counts as complete once its exam gates
              are passed too, so this sits below the bar above by design. */}
          <SignalMetric
            label="Course completion"
            value={`${Math.round(examProfile?.courseCompletionRatePercent ?? 0)}%`}
            sub={`${examProfile?.coursesCompleted ?? 0} of ${examProfile?.coursesAssigned ?? 0} courses`}
            percent={examProfile?.courseCompletionRatePercent ?? 0}
            isLoading={examProfileLoading}
          />
          <SignalMetric
            label="Exam pass rate"
            value={`${Math.round(examProfile?.successRatePercent ?? 0)}%`}
            sub={`across ${examProfile?.totalAttempts ?? 0} attempt${examProfile?.totalAttempts === 1 ? "" : "s"}`}
            percent={examProfile?.successRatePercent ?? 0}
            isLoading={examProfileLoading}
          />
          <SignalMetric
            label="Average score"
            value={`${Math.round(examProfile?.averageScore ?? 0)}%`}
            sub={`${examProfile?.failedExamCount ?? 0} failed`}
            percent={examProfile?.averageScore ?? 0}
            isLoading={examProfileLoading}
          />
          {/* StudentDto carries no learning-hours figure; only the exam-profile endpoint computes
              it, so this has no `student` fallback to fall back to. */}
          <SignalMetric
            label="Learning hours"
            value={`${examProfile?.learningHours ?? 0}`}
            sub={`hrs · last active ${ago(examProfile?.lastActivityAt)}`}
            isLoading={examProfileLoading}
          />
        </div>

        {/* OVERVIEW TAB */}
        {/* OVERVIEW TAB
            Two columns rather than one long stack: the left reads "where does this student
            stand", the right answers "what do I do about it". The eight hero/stat cards this
            replaced are now the signal strip above, which every tab shares. */}
        <TabsContent value="overview">
          {examProfileError ? (
            <Card>
              <CardContent className="py-12 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Could not load this student's progress.
                </p>
                <Button variant="outline" size="sm" onClick={() => refetchExamProfile()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_384px]">
              {/* ── Where the student stands ─────────────────────────────────────── */}
              <div className="min-w-0 space-y-5">
                {examProfileLoading ? (
                  <Skeleton className="h-72 w-full rounded-xl" />
                ) : courseProgress.length > 0 ? (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-primary" /> Course progress
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {courseProgress.filter((c) => c.isCompleted).length} completed ·{" "}
                        {courseProgress.filter((c) => !c.isCompleted && !c.isLocked).length} open ·{" "}
                        {courseProgress.filter((c) => c.isLocked).length} blocked. Open Courses
                        &amp; Tracks for the module detail.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-1">
                      {courseProgress.map((course) => (
                        <div
                          key={`${course.trackId ?? "t"}-${course.courseId}`}
                          className="grid grid-cols-1 items-center gap-2 border-b py-3 last:border-b-0 last:pb-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_150px_auto] md:gap-4"
                        >
                          <div className="min-w-0">
                            <Link
                              to="/team-leader/students/$studentId/courses/$courseId"
                              params={{ studentId, courseId: course.courseId }}
                              className="block truncate text-sm font-semibold hover:text-primary"
                              title={course.courseTitle}
                            >
                              {course.courseTitle}
                            </Link>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {course.trackName ?? "Standalone"} · {course.completedModules}/
                              {course.totalModules} modules
                            </p>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-xs text-muted-foreground">
                              {course.lastAccessedModule
                                ? `Last: ${course.lastAccessedModule.title} · ${ago(course.lastAccessedModule.lastAccessedAt)}`
                                : "Not started yet"}
                            </p>
                            <p className="truncate text-xs text-muted-foreground/70">
                              {course.nextModule
                                ? `Next: ${course.nextModule.title}`
                                : course.totalModules === 0
                                  ? "No published modules"
                                  : "All modules done"}
                            </p>
                          </div>
                          <div className="flex min-w-0 items-center gap-2.5">
                            <Progress
                              value={course.completionPercent}
                              className="h-1.5 flex-1 bg-muted"
                            />
                            <span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums">
                              {course.completionPercent}%
                            </span>
                          </div>
                          <div className="flex justify-start md:justify-end">
                            <CourseStatusBadge course={course} />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : null}

                {checkpointProgress.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" /> Exam gates
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Checkpoints standing between this student and course completion.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {checkpointProgress.map((cp) => (
                        <div
                          key={`${cp.courseId}-${cp.currentCheckpointName ?? "none"}`}
                          className="flex items-center justify-between gap-3 rounded-lg border p-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{cp.courseName}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {cp.currentCheckpointName ?? "No checkpoint reached yet"}
                            </p>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {cp.checkpointStatus}
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* `length || length` evaluates to the NUMBER 0 when both are empty, and React
                    renders a literal "0" for that. Compare explicitly so the guard is a boolean. */}
                {((examProfile?.strongCategories?.length ?? 0) > 0 ||
                  (examProfile?.weakCategories?.length ?? 0) > 0) && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-primary" /> Assessment picture
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Question categories this student is consistently strong or weak on.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div className="min-w-0">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                          Strong areas
                        </p>
                        {(examProfile?.strongCategories?.length ?? 0) === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            No standout strengths yet.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {examProfile?.strongCategories.map((c: string) => (
                              <Badge
                                key={c}
                                className="border-transparent bg-success-muted text-xs text-success hover:bg-success-muted"
                              >
                                {c}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                          Needs attention
                        </p>
                        {(examProfile?.weakCategories?.length ?? 0) === 0 ? (
                          <p className="text-xs text-muted-foreground">No weak areas flagged.</p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {examProfile?.weakCategories.map((c: string) => (
                              <Badge
                                key={c}
                                className="border-transparent bg-destructive-muted text-xs text-destructive hover:bg-destructive-muted"
                              >
                                {c}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* ── The mentoring column ─────────────────────────────────────────── */}
              <div className="min-w-0 space-y-5">
                <Card
                  className={
                    nextMove.tone === "danger"
                      ? "border-destructive/40 bg-destructive-muted/40"
                      : nextMove.tone === "warn"
                        ? "border-accent/60 bg-warning-muted/40"
                        : undefined
                  }
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <nextMove.icon
                        className={`h-4 w-4 ${
                          nextMove.tone === "danger"
                            ? "text-destructive"
                            : nextMove.tone === "warn"
                              ? "text-warning"
                              : "text-success"
                        }`}
                      />
                      Your next move
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold">{nextMove.title}</p>
                      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                        {nextMove.body}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {nextMove.actions.map((action) => (
                        <Button
                          key={action.label}
                          size="sm"
                          variant={action.primary ? "default" : "outline"}
                          className="gap-2"
                          onClick={action.onClick}
                        >
                          <action.icon className="h-4 w-4" />
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* The contact log used to live only behind the CRM tab, so the record of what had
                    already been tried was a click away from the progress that motivates it. */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" /> Contact log
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {activityEntries.length === 0
                        ? "Nothing logged yet."
                        : `${activityEntries.length} entr${activityEntries.length === 1 ? "y" : "ies"}, most recent first.`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {contactLog.slice(0, 3).map((entry) => {
                      const Icon = CONTACT_ICON[entry.action] ?? CONTACT_ICON.other;
                      return (
                        <div key={entry.id} className="flex items-start gap-2.5">
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-xs font-bold">
                                {CONTACT_LABELS[entry.action] ?? "Contact"}
                              </span>
                              <span className="shrink-0 text-[11px] text-muted-foreground/70">
                                {ago(entry.created_at)}
                              </span>
                            </div>
                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {entry.notes?.trim() ||
                                CONTACT_OUTCOMES[entry.outcome] ||
                                "No note recorded"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => setActiveTab("crm")}
                      >
                        <NotebookPen className="h-4 w-4" /> Log a contact
                      </Button>
                      {activityEntries.length > 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => setActiveTab("crm")}
                        >
                          View all {activityEntries.length}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {certificates.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Award className="h-4 w-4 text-primary" /> Certificates (
                        {certificates.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* `CertificateDto` carries snake_case field names by explicit
                          `[JsonPropertyName]` override — it is shared with the public
                          /verify/:certCode contract. This card previously read `issuedAt` and
                          `certificateCode`, neither of which exists on the payload, so every
                          certificate rendered an em dash for its date and never showed a code. */}
                      {certificates.map((cert: TlCertificate) => {
                        const issued = cert.issued_at ?? cert.created_at;
                        return (
                          <div
                            key={cert.id}
                            className="flex items-center justify-between gap-3 rounded-lg border p-3"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">
                                {cert.custom_title ||
                                  cert.title ||
                                  cert.course?.title ||
                                  cert.track?.name ||
                                  "Certificate"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {issued ? new Date(issued).toLocaleDateString() : "—"}
                              </p>
                            </div>
                            {cert.cert_code && (
                              <Badge variant="secondary" className="shrink-0 font-mono text-[10px]">
                                {cert.cert_code}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* COURSES & TRACKS TAB */}
        <TabsContent value="courses" className="space-y-6">
          {examProfileLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : examProfileError ? (
            <Card>
              <CardContent className="py-12 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Could not load this student's courses and tracks.
                </p>
                <Button variant="outline" size="sm" onClick={() => refetchExamProfile()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : currentCourses.length === 0 && currentTracks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="font-semibold">No courses or tracks assigned</p>
                <p className="text-xs mt-1">
                  Assign this student a course or track from My Groups to see it here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Roll-up strip so the counts are readable without scanning every card. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Courses assigned", value: examProfile?.coursesAssigned ?? 0 },
                  { label: "Completed", value: examProfile?.coursesCompleted ?? 0 },
                  { label: "In progress", value: examProfile?.coursesInProgress ?? 0 },
                  { label: "Not started", value: examProfile?.coursesNotStarted ?? 0 },
                ].map((stat) => (
                  <Card key={stat.label}>
                    <CardContent className="pt-6">
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Older API builds return only `currentCourses` (names) with no `courseProgress`.
                  Fall back to the flat name list rather than rendering an empty tab. */}
              {courseProgress.length === 0 ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-primary" /> Courses (
                      {currentCourses.length})
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Detailed progress is unavailable from this API build.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {currentCourses.map((courseName: string) => {
                      const cp = checkpointProgress.find((c) => c.courseName === courseName);
                      return (
                        <div
                          key={courseName}
                          className="flex items-center justify-between gap-3 rounded-lg border p-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{courseName}</p>
                            {cp && (
                              <p className="text-xs text-muted-foreground truncate">
                                {cp.currentCheckpointName ?? "No checkpoint reached yet"}
                              </p>
                            )}
                          </div>
                          {cp && (
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {cp.checkpointStatus}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {courseGroups.standalone.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-primary" /> Standalone courses (
                        {courseGroups.standalone.length})
                      </h3>
                      {courseGroups.standalone.map((course) => (
                        <CourseProgressCard
                          key={course.courseId}
                          course={course}
                          studentId={studentId}
                        />
                      ))}
                    </div>
                  )}

                  {courseGroups.tracks.map(([trackName, courses]) => {
                    const done = courses.filter((c) => c.isCompleted).length;
                    return (
                      <div key={trackName} className="space-y-3">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <Layers className="h-4 w-4 text-primary" /> {trackName}
                          <span className="font-normal text-xs text-muted-foreground">
                            {done}/{courses.length} courses complete
                          </span>
                        </h3>
                        {courses.map((course) => (
                          <CourseProgressCard
                            key={`${course.trackId ?? "t"}-${course.courseId}`}
                            course={course}
                            studentId={studentId}
                          />
                        ))}
                      </div>
                    );
                  })}
                </>
              )}

              {/* A track with no courses attached yet still needs to show up somewhere — it has
                  no card above, so list every assigned track name here. */}
              {currentTracks.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-primary" /> Assigned tracks (
                      {currentTracks.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {currentTracks.map((trackName: string) => (
                        <Badge key={trackName} variant="secondary" className="text-xs">
                          {trackName}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* EXAMS TAB */}
        {/* EXAMS TAB
            Same two-column frame as Overview: the left is the record, the right is what to do
            about it. Scores carry a bar so a column of attempts reads as a trajectory rather than
            a list of numbers. */}
        <TabsContent value="exams">
          {examProfileLoading ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : examProfileError ? (
            <Card>
              <CardContent className="py-12 text-center space-y-3">
                <p className="text-sm text-muted-foreground">Could not load exam history.</p>
                <Button variant="outline" size="sm" onClick={() => refetchExamProfile()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : assignedExams.length === 0 && examHistory.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="font-semibold">No exams assigned</p>
                <p className="text-xs mt-1">
                  Assign an exam to this student — or to their course, track or group — for it to
                  appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_384px]">
                <div className="min-w-0 space-y-5">
                  {/* Assigned exams, including ones never attempted — attempt history alone hid
                      everything the student had not started yet. */}
                  {assignedExams.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-primary" /> Assigned exams (
                          {assignedExams.length})
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Available to this student right now, whether or not they have attempted
                          it.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-1">
                        {assignedExams.map((exam) => (
                          <div
                            key={exam.id}
                            className="flex flex-col gap-2 border-b py-3 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">{exam.title}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {exam.courseTitle ? `${exam.courseTitle} · ` : ""}
                                {exam.attemptsUsed}/{exam.maxAttempts} attempts used
                                {exam.lastScore != null && ` · last ${Math.round(exam.lastScore)}%`}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              {exam.attemptsUsed === 0 && (
                                <Badge variant="secondary" className="text-[10px]">
                                  Not started
                                </Badge>
                              )}
                              {exam.isLocked ? (
                                <Badge
                                  variant="outline"
                                  className="border-warning/40 text-[10px] text-warning"
                                  title={exam.lockReason ?? undefined}
                                >
                                  <Lock className="mr-1 h-3 w-3" /> Locked
                                </Badge>
                              ) : exam.attemptsRemaining === 0 ? (
                                <Badge variant="destructive" className="text-[10px]">
                                  No attempts left
                                </Badge>
                              ) : !exam.withinWindow ? (
                                <Badge variant="outline" className="text-[10px]">
                                  Outside window
                                </Badge>
                              ) : (
                                <Badge className="text-[10px]">Available</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {examHistory.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" /> Attempt history (
                          {examHistory.length})
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {examProfile?.totalAttempts ?? examHistory.length} attempts ·{" "}
                          {examHistory.filter((e) => e.passed === true).length} passed
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-1">
                        {examHistory.map((exam) => (
                          <div
                            key={exam.sessionId}
                            className="grid grid-cols-1 items-center gap-2 border-b py-3 last:border-b-0 last:pb-0 md:grid-cols-[24px_minmax(0,1fr)_170px_auto] md:gap-4"
                          >
                            {exam.passed === true ? (
                              <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
                            ) : exam.passed === false ? (
                              <XCircle className="h-5 w-5 shrink-0 text-destructive" />
                            ) : (
                              <Clock className="h-5 w-5 shrink-0 text-muted-foreground" />
                            )}
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">{exam.examTitle}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {exam.completedAt
                                  ? `Completed ${new Date(exam.completedAt).toLocaleDateString()}`
                                  : `Started ${new Date(exam.startedAt).toLocaleDateString()}`}
                                {exam.violationCount > 0 &&
                                  ` · ${exam.violationCount} proctoring violation${exam.violationCount === 1 ? "" : "s"}`}
                              </p>
                            </div>
                            <div className="flex min-w-0 items-center gap-2.5">
                              {exam.score != null ? (
                                <>
                                  <Progress
                                    value={exam.score}
                                    className={`h-1.5 flex-1 bg-muted ${
                                      exam.passed === false
                                        ? "[&>div]:bg-destructive"
                                        : "[&>div]:bg-success"
                                    }`}
                                  />
                                  <span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums">
                                    {Math.round(exam.score)}%
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground">Not scored</span>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center justify-start gap-2 md:justify-end">
                              {exam.violationCount > 0 && (
                                <Badge
                                  variant="outline"
                                  className="border-warning/40 text-[10px] text-warning"
                                >
                                  <AlertTriangle className="mr-1 h-3 w-3" />
                                  {exam.violationCount}
                                </Badge>
                              )}
                              <Badge
                                variant={
                                  exam.passed === true
                                    ? "default"
                                    : exam.passed === false
                                      ? "destructive"
                                      : "secondary"
                                }
                                className="text-[10px]"
                              >
                                {exam.passed === true
                                  ? "Passed"
                                  : exam.passed === false
                                    ? "Failed"
                                    : exam.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="min-w-0 space-y-5">
                  {blockedExams.length > 0 && (
                    <Card className="border-destructive/40 bg-destructive-muted/40">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-destructive" /> Out of attempts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <p className="text-[13px] leading-relaxed text-muted-foreground">
                          Every attempt is spent on{" "}
                          <span className="font-semibold text-foreground">
                            {blockedExams.map((e) => e.title).join(", ")}
                          </span>{" "}
                          without a pass. Nothing moves until a reattempt is granted or the course
                          content is reset — both live on the attempt timeline below, per sitting.
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Proctoring is stated as events on named attempts rather than a bare count:
                      "2 violations" says nothing about which sitting is in question. */}
                  {examHistory.some((e) => e.violationCount > 0) && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-primary" /> Proctoring
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {examHistory.reduce((sum, e) => sum + (e.violationCount ?? 0), 0)}{" "}
                          violations across {examHistory.filter((e) => e.violationCount > 0).length}{" "}
                          attempt(s).
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {examHistory
                          .filter((e) => e.violationCount > 0)
                          .map((e) => (
                            <div
                              key={`v-${e.sessionId}`}
                              className="flex items-center justify-between gap-3 rounded-lg bg-warning-muted/60 p-2.5"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-xs font-bold">{e.examTitle}</p>
                                <p className="truncate text-[11px] text-muted-foreground">
                                  {e.completedAt
                                    ? new Date(e.completedAt).toLocaleDateString()
                                    : new Date(e.startedAt).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge
                                variant="outline"
                                className="shrink-0 border-warning/40 text-[10px] text-warning"
                              >
                                {e.violationCount}
                              </Badge>
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  )}

                  {scoreTrend.length > 1 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <TrendingDown className="h-4 w-4 text-primary" /> Score trend
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Last {scoreTrend.length} scored attempts, oldest first.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex h-28 items-end gap-2">
                          {scoreTrend.map((point) => (
                            <div
                              key={point.key}
                              className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
                              title={`${point.title} — ${Math.round(point.score)}%`}
                            >
                              <span className="text-[11px] font-bold tabular-nums">
                                {Math.round(point.score)}%
                              </span>
                              <div
                                className={`w-full rounded-t ${point.passed === false ? "bg-destructive" : "bg-success"}`}
                                style={{ height: `${Math.max(4, point.score * 0.7)}px` }}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex gap-2 border-t pt-2">
                          {scoreTrend.map((point) => (
                            <span
                              key={`l-${point.key}`}
                              className="min-w-0 flex-1 truncate text-center text-[10px] text-muted-foreground/70"
                            >
                              {point.label}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Ported from `/team-leader/exams/student/$studentId`, which this page now replaces.
                  It was the one capability that route had and this one did not: a server-paginated
                  audit stream of every attempt event, with exam / event-type / date-range filters
                  and the four moderation actions per row. Mounted inside the Exams tab, so it costs
                  nothing until the tab is opened. */}
              <StudentAttemptTimeline
                studentId={studentId}
                studentName={examProfile?.studentName ?? student?.fullName}
                email={examProfile?.email ?? student?.email}
                examHistory={examHistory}
              />
            </div>
          )}
        </TabsContent>

        {/* CRM TAB — the same contact log the admin roster uses, so an entry logged by either
            role is visible to both. */}
        {/* ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="space-y-4">
          <StudentAssignmentsPanel studentId={studentId} />
        </TabsContent>

        <TabsContent value="crm" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" /> CRM Contact Log
              </CardTitle>
              <CardDescription className="text-xs">
                Calls, messages and follow-ups for this student. Shared with the admin student
                roster — entries logged there appear here, and vice versa.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StudentActivityLog studentId={studentId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
